import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Layers, Filter, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MrpView = () => {
  const [requirements, setRequirements] = useState([]);
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    item_id: '',
    category: '',
    need_date: '',
    level: '',
    qty_min: '',
    qty_max: '',
    customer_id: ''
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('need_date');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchRequirements();
  }, [pagination.page, sortBy, sortOrder, filters.item_id, filters.need_date, filters.customer_id]);

  // Client-side filters for category, level, qty range
  useEffect(() => {
    let filtered = [...requirements];

    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(r => {
        const reqItem = items.find(i => i.id === r.item_id);
        return reqItem?.category === filters.category;
      });
    }

    if (filters.level !== '') {
      filtered = filtered.filter(r => r.level === parseInt(filters.level));
    }

    if (filters.qty_min) {
      filtered = filtered.filter(r => r.qty >= parseFloat(filters.qty_min));
    }

    if (filters.qty_max) {
      filtered = filtered.filter(r => r.qty <= parseFloat(filters.qty_max));
    }

    setFilteredRequirements(filtered);
  }, [requirements, filters.category, filters.level, filters.qty_min, filters.qty_max, items]);

  const fetchMasterData = async () => {
    try {
      const [itemRes, custRes] = await Promise.all([
        axios.get(`${API}/items`),
        axios.get(`${API}/customers`)
      ]);
      setItems(itemRes.data);
      setCustomers(custRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchRequirements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      // Add server-side filters
      if (filters.item_id && filters.item_id !== 'all') params.append('item_id', filters.item_id);
      if (filters.need_date) params.append('need_date', filters.need_date);
      if (filters.customer_id && filters.customer_id !== 'all') params.append('customer_id', filters.customer_id);
      
      const response = await axios.get(`${API}/mrp/requirements?${params.toString()}`);
      
      if (response.data.data) {
        setRequirements(response.data.data);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setRequirements(response.data);
      }
    } catch (error) {
      console.error('Error fetching MRP data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      item_id: '',
      category: '',
      need_date: '',
      level: '',
      qty_min: '',
      qty_max: '',
      customer_id: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getItemCategory = (id) => {
    const item = items.find(i => i.id === id);
    return item?.category || 'N/A';
  };

  const getCustomerName = (id) => {
    if (!id) return '-';
    const customer = customers.find(c => c.id === id);
    return customer?.name || '-';
  };

  const getParentItemInfo = (parentItemId) => {
    if (!parentItemId) return '-';
    const item = items.find(i => i.id === parentItemId);
    return item ? `${item.part_number}` : '-';
  };

  const getLevelBadgeColor = (level) => {
    if (level === 0) return 'bg-blue-100 text-blue-800';
    if (level === 1) return 'bg-green-100 text-green-800';
    if (level === 2) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Items filtered by selected customer for the Item dropdown
  const filteredItemsForDropdown = (() => {
    if (!filters.customer_id || filters.customer_id === 'all') return items;
    // Get FG items for this customer
    const customerFgItems = items.filter(i => i.category === 'FG' && i.customer_id === filters.customer_id);
    const customerFgIds = new Set(customerFgItems.map(i => i.id));
    // Get all items that appear in MRP requirements for this customer
    const reqItemIds = new Set(requirements.map(r => r.item_id));
    // Include FG items + all items from current requirements (these are the BOM children)
    return items.filter(i => customerFgIds.has(i.id) || reqItemIds.has(i.id));
  })();

  const hasActiveFilters = Object.values(filters).some(v => v !== '');
  const uniqueCategories = [...new Set(filteredItemsForDropdown.map(i => i.category))];
  const maxLevel = Math.max(...requirements.map(r => r.level), 0);

  return (
    <Card data-testid="mrp-requirements-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-600" />
            <div>
              <CardTitle>MRP Requirements</CardTitle>
              <CardDescription>Material requirements from BOM explosion with lead times</CardDescription>
            </div>
          </div>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              <X className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Section */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-600" />
            <h3 className="font-semibold text-sm text-slate-700">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Customer</label>
              <Select
                data-testid="filter-customer"
                value={filters.customer_id}
                onValueChange={(value) => {
                  setFilters({ ...filters, customer_id: value, item_id: '', category: '', level: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Item</label>
              <SearchableItemSelect
                items={filteredItemsForDropdown}
                value={filters.item_id}
                onValueChange={(value) => {
                  setFilters({ ...filters, item_id: value });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                placeholder={filters.customer_id && filters.customer_id !== 'all' ? "Customer items" : "All items"}
                dataTestId="filter-item"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Category</label>
              <Select
                data-testid="filter-category"
                value={filters.category}
                onValueChange={(value) => setFilters({ ...filters, category: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {uniqueCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Need Date</label>
              <Input
                data-testid="filter-need-date"
                type="date"
                value={filters.need_date}
                onChange={(e) => setFilters({ ...filters, need_date: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">BOM Level</label>
              <Select
                data-testid="filter-level"
                value={filters.level}
                onValueChange={(value) => setFilters({ ...filters, level: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  {[...Array(maxLevel + 1)].map((_, i) => (
                    <SelectItem key={i} value={i.toString()}>Level {i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Qty Min</label>
              <Input
                data-testid="filter-qty-min"
                type="number"
                placeholder="Min"
                value={filters.qty_min}
                onChange={(e) => setFilters({ ...filters, qty_min: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Qty Max</label>
              <Input
                data-testid="filter-qty-max"
                type="number"
                placeholder="Max"
                value={filters.qty_max}
                onChange={(e) => setFilters({ ...filters, qty_max: e.target.value })}
                className="h-9"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : filteredRequirements.length === 0 ? (
          <div data-testid="no-requirements" className="text-center py-8 text-slate-500">
            {hasActiveFilters ? 'No requirements match the selected filters.' : 'No MRP requirements. Run MRP first.'}
          </div>
        ) : (
          <div>
            <div className="mb-2 text-sm text-slate-600">
              Showing <strong>{filteredRequirements.length}</strong> of <strong>{requirements.length}</strong> records
            </div>
            <div className="overflow-auto max-h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Level</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Parent Item</TableHead>
                    <TableHead>Need Date</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequirements.map((req) => (
                    <TableRow key={req.id} data-testid={`mrp-row-${req.id}`}>
                      <TableCell>
                        <Badge className={getLevelBadgeColor(req.level)}>
                          <Layers className="w-3 h-3 mr-1" />
                          L{req.level}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{getCustomerName(req.customer_id)}</span>
                      </TableCell>
                      <TableCell className="font-medium">{getItemInfo(req.item_id)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{getItemCategory(req.item_id)}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">{getParentItemInfo(req.parent_item_id)}</span>
                      </TableCell>
                      <TableCell>{req.need_date}</TableCell>
                      <TableCell className="text-right font-semibold">{Math.round(req.qty)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MrpView;
